// src/GlobalReceiptScanner.js
// Super-admin GLOBAL receipt scanner. Reached from the mobile nav's upload icon.
// Snap/select receipts (no project context) → each routes by its detected PO.
// The admin reviews every receipt in one batch and makes the call per receipt:
//   • Save (N) Expense    → books them now; the project's PM is notified
//                           "approved by Admin".
//   • Acknowledge Re-route → hands them to the detected project's PM review queue
//                           (the existing "Receipts need your review" flow, just
//                           with a "From Admin" badge).
// No-PO receipts show "NO PO Detected", start unchecked, and (when checked) go to
// the Expense Ledger's Unallocated bucket.
//
// Fully self-contained. Reuses ReceiptCropper for the crop/auto-detect step.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Upload, Camera, Check, CornerUpRight, Loader2, Boxes, Trash2 } from 'lucide-react';
import { API_BASE } from './apiBase';
import ReceiptCropper from './projects/project_finances/ReceiptCropper';

const INV_SCAN_REGION = 'global-inv-scan-region';

const isImage = (f) =>
  f.type?.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name || '');

// Persisted across close/reopen so an in-flight scan can be resumed and the nav
// icon can pulse while it's pending. Cleared on cancel/save/re-route.
const PENDING_KEY = 'adminScanPendingId';
const persistPending = (id) => {
  if (id) localStorage.setItem(PENDING_KEY, id);
  else localStorage.removeItem(PENDING_KEY);
  window.dispatchEvent(new Event('adminScan:changed'));
};

export default function GlobalReceiptScanner({ onClose, initialPendingId, receiptsOnly = false }) {
  const [cropQueue, setCropQueue] = useState([]);   // images waiting for the cropper
  const [captured, setCaptured] = useState([]);     // ready-to-upload Files
  const [view, setView] = useState('capture');      // 'capture' | 'processing' | 'review'
  const [pendingId, setPendingId] = useState(null);
  const [batchData, setBatchData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const invScannerRef = useRef(null);

  const token = localStorage.getItem('token');

  // ================= INVENTORY RAPID SCAN =================
  // The camera stays open and each read drops into a running list — scan a whole
  // bucket, then review quantities once and submit. Known barcodes (matched on
  // item name/alias) increment their line; unknowns are collected (not blocking)
  // and resolved via "Add New Item" at review. Submitting posts the batch through
  // the existing /confirm endpoint (updates stock + writes logs).
  const [invItems, setInvItems] = useState([]);     // inventory for local matching
  const [scanList, setScanList] = useState([]);      // accumulated scans
  const [direction, setDirection] = useState('withdraw'); // session default
  const [invReview, setInvReview] = useState(false); // scanning vs review
  const [toast, setToast] = useState(null);          // per-scan feedback
  const [pulse, setPulse] = useState(null);          // tap-to-focus ripple
  const [addForm, setAddForm] = useState(null);      // inline Add New Item form
  const lastScanRef = useRef({ code: null, t: 0 });
  const toastTimer = useRef(null);
  const handlerRef = useRef(null);

  // Load inventory once when entering scan mode (for name/alias matching).
  useEffect(() => {
    if (view !== 'barcode') return;
    axios.get(`${API_BASE}/items`, { headers: { Authorization: token } })
      .then(res => setInvItems(Array.isArray(res.data) ? res.data : (res.data.items || [])))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const flashToast = (msg, ok) => {
    setToast({ msg, ok });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1200);
  };

  // A single decode. Debounced so one physical barcode (many camera frames)
  // counts once; same item re-scanned bumps its quantity.
  const onBarcode = (code) => {
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.t < 1500) return;
    lastScanRef.current = { code, t: now };
    const lower = String(code).toLowerCase();
    const it = invItems.find(x =>
      x.name?.toLowerCase() === lower ||
      (Array.isArray(x.aliases) && x.aliases.some(a => String(a).toLowerCase() === lower))
    );
    if (it) {
      setScanList(prev => {
        const ex = prev.find(r => r.itemId === it._id);
        if (ex) return prev.map(r => r.itemId === it._id ? { ...r, quantity: r.quantity + 1 } : r);
        return [...prev, { key: it._id, itemId: it._id, name: it.name, barcode: code, quantity: 1, action: direction, known: true, location: it.location || '', price: it.price || 0 }];
      });
      if (navigator.vibrate) navigator.vibrate(60);
      flashToast(`✓ ${it.name}`, true);
    } else {
      setScanList(prev => {
        const ex = prev.find(r => !r.known && r.barcode === code);
        if (ex) return prev.map(r => (!r.known && r.barcode === code) ? { ...r, quantity: r.quantity + 1 } : r);
        return [...prev, { key: 'u-' + code, itemId: null, name: code, barcode: code, quantity: 1, action: direction, known: false, location: '', price: 0 }];
      });
      if (navigator.vibrate) navigator.vibrate([40, 40, 40]);
      flashToast(`Unknown barcode — resolve at review`, false);
    }
  };
  handlerRef.current = onBarcode; // always call the freshest closure

  const startInvScanner = useCallback(async () => {
    try {
      const scanner = new Html5Qrcode(INV_SCAN_REGION);
      invScannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 180 } },
        (decoded) => handlerRef.current && handlerRef.current(decoded),
        () => {} // per-frame misses are normal
      );
      // Prefer continuous autofocus where supported.
      try { await scanner.applyVideoConstraints({ advanced: [{ focusMode: 'continuous' }] }); } catch (_) {}
    } catch (_) {
      setError('Could not start the camera. Check camera permissions.');
    }
  }, []);

  const stopInvScanner = useCallback(async () => {
    const s = invScannerRef.current;
    invScannerRef.current = null;
    if (s) { try { await s.stop(); } catch (_) {} try { s.clear(); } catch (_) {} }
  }, []);

  // Run the camera only while actively scanning (not in review).
  useEffect(() => {
    if (view !== 'barcode' || invReview) return;
    startInvScanner();
    return () => { stopInvScanner(); };
  }, [view, invReview, startInvScanner, stopInvScanner]);

  // Tap the video to refocus: re-apply continuous focus; if the phone ignores
  // focus constraints (common on iOS), fall back to a quick camera re-acquire.
  const refocus = async (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPulse({ x: e.clientX - rect.left, y: e.clientY - rect.top, id: Date.now() });
    setTimeout(() => setPulse(null), 500);
    const s = invScannerRef.current;
    try {
      if (s && typeof s.applyVideoConstraints === 'function') {
        await s.applyVideoConstraints({ advanced: [{ focusMode: 'continuous' }] });
        return;
      }
    } catch (_) { /* fall through */ }
    await stopInvScanner();
    startInvScanner();
  };

  const setLineQty = (key, q) =>
    setScanList(prev => prev.map(r => r.key === key ? { ...r, quantity: Math.max(0, q) } : r));
  const toggleLineAction = (key) =>
    setScanList(prev => prev.map(r => r.key === key ? { ...r, action: r.action === 'withdraw' ? 'deposit' : 'withdraw' } : r));
  const removeLine = (key) => setScanList(prev => prev.filter(r => r.key !== key));

  // Add an unknown scanned barcode to inventory INLINE (no navigation) so several
  // new items can be added in one session. Creates the catalog item with the
  // barcode saved as an alias, then drops the row from the unknown list.
  const saveNewItem = async (row) => {
    if (!addForm || !addForm.name.trim()) { setError('Item name is required.'); return; }
    setBusy(true); setError(null);
    try {
      const body = {
        name: addForm.name.trim(),
        aliases: [row.barcode],
        quantity: Number(addForm.quantity) || 0,
        location: addForm.location || '',
        price: Number(addForm.price) || 0,
      };
      const res = await axios.post(`${API_BASE}/items`, body, { headers: { Authorization: token } });
      const created = res.data && res.data._id ? res.data : { ...body, _id: `new-${Date.now()}` };
      setInvItems(prev => [...prev, created]); // a re-scan now matches
      removeLine(row.key);
      setAddForm(null);
      flashToast(`Added ${body.name}`, true);
    } catch (_) {
      setError('Could not add item.');
    } finally {
      setBusy(false);
    }
  };

  const submitScan = async () => {
    const known = scanList.filter(r => r.known && r.quantity > 0);
    if (!known.length) { setError('Nothing to submit yet.'); return; }
    setBusy(true); setError(null);
    try {
      const cart = known.map(r => ({ itemId: r.itemId, name: r.name, location: r.location, action: r.action, quantity: r.quantity, price: r.price }));
      await axios.post(`${API_BASE}/confirm`, { cart }, { headers: { Authorization: token } });
      const leftoverUnknown = scanList.filter(r => !r.known);
      if (leftoverUnknown.length) {
        setScanList(leftoverUnknown);
        setBusy(false);
        flashToast(`Submitted ${known.length}. ${leftoverUnknown.length} unknown left to add.`, true);
      } else {
        close();
      }
    } catch (err) {
      setError('Submit failed.');
      setBusy(false);
    }
  };

  const totalScanned = scanList.reduce((s, r) => s + r.quantity, 0);

  // Resume an in-flight scan that was X-ed out of: jump straight to polling, which
  // lands on the review card (or stays on "reading…" if Grok is still working).
  useEffect(() => {
    if (initialPendingId) {
      setPendingId(initialPendingId);
      setView('processing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- file pick → queue images to the cropper, PDFs straight through ----
  const enqueueFiles = (fileList) => {
    const list = Array.from(fileList || []);
    const imgs = list.filter(isImage);
    const others = list.filter(f => !isImage(f));
    if (others.length) setCaptured(prev => [...prev, ...others]); // PDFs skip the cropper
    if (imgs.length) setCropQueue(prev => [...prev, ...imgs]);     // images go through crop
  };
  const onPick = (e) => { enqueueFiles(e.target.files); e.target.value = ''; };

  const addCaptured = (file) => {
    setCaptured(prev => [...prev, file]);
    setCropQueue(prev => prev.slice(1));
  };

  // ---- upload everything captured (admin scan, no project) ----
  const uploadAndScan = async () => {
    if (!captured.length || busy) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      captured.forEach(f => fd.append('file', f));
      fd.append('adminScan', 'true');
      const res = await axios.post(`${API_BASE}/expenses/extract-receipt`, fd, {
        headers: { Authorization: token, 'Content-Type': 'multipart/form-data' },
      });
      setPendingId(res.data.pendingId);
      persistPending(res.data.pendingId); // resumable + makes the nav icon pulse
      setView('processing');
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  // ---- poll the admin batch until Grok finishes ----
  useEffect(() => {
    if (view !== 'processing' || !pendingId) return;
    let stop = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API_BASE}/expenses/pending-one/${pendingId}`, {
          headers: { Authorization: token },
        });
        const p = res.data;
        if (p.status === 'Ready') {
          const data = Array.isArray(p.extractedData) ? p.extractedData : [];
          // Surface the detected project's PO in the editable "PO #" field when the
          // receipt itself carried none (e.g. a return matched to its original).
          const seeded = data.map(r => ({ ...r, poNumber: r.poNumber || r.adminDetectedPO || '' }));
          setBatchData(seeded);
          // Items routed to a project (by PO, or a return matched to its original)
          // start checked; unrouted ones (no PO / orphan return) start unchecked.
          setSelected(data.filter(r => r.adminDetectedProject).map(r => r.id));
          setView('review');
          stop = true;
        } else if (p.status === 'Failed') {
          setError(p.failureReason || 'Scan failed.');
          setView('capture');
          stop = true;
        }
      } catch (_) { /* keep polling */ }
    };
    tick();
    const iv = setInterval(() => { if (!stop) tick(); else clearInterval(iv); }, 2500);
    return () => clearInterval(iv);
  }, [view, pendingId, token]);

  const toggleSelect = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Editing the PO is a manual override. If it differs from what was auto-detected,
  // drop the detection so the typed PO drives routing (re-route + save both honor it).
  const updatePO = (id, val) =>
    setBatchData(prev => prev.map(r => {
      if (r.id !== id) return r;
      const changed = String(val).trim() !== String(r.adminDetectedPO || '').trim();
      if (!changed) return { ...r, poNumber: val };
      return { ...r, poNumber: val, manualPO: true, adminDetectedProject: null, adminDetectedTitle: null, adminDetectedPO: null };
    }));

  const reset = () => {
    setCropQueue([]); setCaptured([]); setView('capture');
    setPendingId(null); setBatchData([]); setSelected([]); setError(null);
  };
  // X = leave it. If a batch is already on the server it STAYS pending (the nav
  // icon keeps pulsing and reopening resumes it). Only clears the in-memory,
  // not-yet-uploaded captures.
  const close = () => { reset(); onClose && onClose(); };

  // Cancel = terminate this scan: delete the pending batch on the server, stop
  // the icon pulsing, and close.
  const cancelProcess = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (pendingId) {
        await axios.delete(`${API_BASE}/expenses/pending/${pendingId}`, { headers: { Authorization: token } });
      }
    } catch (_) { /* deleting is best-effort */ }
    persistPending(null);
    window.dispatchEvent(new Event('notifications:refresh'));
    setBusy(false);
    close();
  };

  // Cleared once the batch has been saved or handed off.
  const finishAndClear = () => { persistPending(null); close(); };

  const splitSelected = useCallback(() => {
    const chosen = batchData.filter(r => selected.includes(r.id));
    const leftovers = batchData.filter(r => !selected.includes(r.id));
    return { chosen, leftovers };
  }, [batchData, selected]);

  // ---- Save (N) Expense: book them; PM notified "approved by Admin" ----
  const handleSave = async () => {
    if (busy) return;
    const { chosen, leftovers } = splitSelected();
    if (!chosen.length) { setError('Select at least one receipt.'); return; }
    setBusy(true); setError(null);
    try {
      const expenses = chosen.map(r => ({
        ...r,
        amount: Number(r.subtotal != null ? r.subtotal : (r.totalAmount || 0)),
        subtotal: Number(r.subtotal != null ? r.subtotal : (r.totalAmount || 0)),
        totalAmount: Number(r.totalAmount || 0),
        category: r.category || (r.store === 'Permit' ? 'Permits' : 'Materials'),
      }));
      await axios.post(`${API_BASE}/expenses/batch`, {
        expenses, pendingId, leftovers, fromAdmin: true,
      }, { headers: { Authorization: token } });
      window.dispatchEvent(new Event('notifications:refresh'));
      finishAndClear();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
      setBusy(false);
    }
  };

  // ---- Acknowledge Re-route: hand selected to their PMs (From Admin badge) ----
  const handleReroute = async () => {
    if (busy) return;
    const { chosen, leftovers } = splitSelected();
    if (!chosen.length) { setError('Select at least one receipt.'); return; }
    setBusy(true); setError(null);
    try {
      await axios.post(`${API_BASE}/expenses/admin-reroute`, {
        items: chosen, pendingId, leftovers,
      }, { headers: { Authorization: token } });
      window.dispatchEvent(new Event('notifications:refresh'));
      finishAndClear();
    } catch (err) {
      setError(err.response?.data?.error || 'Re-route failed.');
      setBusy(false);
    }
  };

  // ---------- CROP STEP (one image at a time) ----------
  if (cropQueue.length > 0) {
    return (
      <ReceiptCropper
        file={cropQueue[0]}
        remaining={cropQueue.length}
        onDone={addCaptured}
        onUseOriginal={() => addCaptured(cropQueue[0])}
        onCancelAll={() => setCropQueue([])}
      />
    );
  }

  const selectedCount = selected.length;
  // Total reflects only the CHECKED receipts — uncheck one and it drops out.
  const batchTotal = batchData.reduce((s, r) => (selected.includes(r.id) ? s + (Number(r.totalAmount) || 0) : s), 0);

  return (
    <div className="fixed inset-0 z-[120] flex md:items-center md:justify-center md:p-4 md:bg-black/60 md:backdrop-blur-sm">
      <div className="bg-zinc-900 w-full h-full md:h-auto md:max-w-2xl md:max-h-[90vh] md:rounded-2xl md:border md:border-zinc-800 md:shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 max-md:px-4 py-5 max-md:py-3 border-b border-zinc-800 shrink-0">
          <h2 className="text-white font-bold text-xl max-md:text-base flex items-center gap-2">
            <Upload size={20} className="text-indigo-400" />
            {view === 'review' ? 'Review Batch Upload' : view === 'barcode' ? 'Scan Inventory' : 'Scanner'}
          </h2>
          <button onClick={close} className="w-10 h-10 flex items-center justify-center text-zinc-500 hover:text-white active:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Camera-first capture (mobile snaps a photo; desktop opens a file picker) */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          multiple
          className="hidden"
          onChange={onPick}
        />

        {/* ============ CAPTURE — two clean buttons, no drop-zone ============ */}
        {view === 'capture' && (
          <div className="flex-1 overflow-y-auto p-6 flex flex-col">
            {captured.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 py-10">
                <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                  <Camera size={30} className="text-indigo-400" />
                </div>
                <div className="flex flex-col gap-3 w-full max-w-xs">
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="px-6 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    <Camera size={18} /> Scan Receipt
                  </button>
                  {!receiptsOnly && (
                    <button
                      onClick={() => { setError(null); setScanList([]); setInvReview(false); setView('barcode'); }}
                      className="px-6 py-4 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                    >
                      <Boxes size={18} /> Scan Inventory
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <p className="text-zinc-400 text-sm mb-3">{captured.length} receipt{captured.length === 1 ? '' : 's'} ready.</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {captured.map((f, i) => (
                    <div key={i} className="px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-300 flex items-center gap-2">
                      <Check size={13} className="text-emerald-400" /> {f.name?.slice(0, 22) || `Receipt ${i + 1}`}
                    </div>
                  ))}
                </div>
                <div className="mt-auto flex flex-col gap-3">
                  <button onClick={() => fileRef.current?.click()} className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                    <Camera size={18} /> Scan more receipts
                  </button>
                  <button onClick={uploadAndScan} disabled={busy} className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                    Upload ({captured.length}) Scan
                  </button>
                </div>
              </>
            )}
            {error && <p className="text-red-400 text-xs mt-4 text-center">{error}</p>}
          </div>
        )}

      {/* ============ PROCESSING ============ */}
      {view === 'processing' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-zinc-300 p-6">
          <Loader2 size={36} className="animate-spin text-indigo-400" />
          <p className="text-sm">Reading your receipt{captured.length === 1 ? '' : 's'}…</p>
          <p className="text-xs text-zinc-500 text-center max-w-xs">You can close this — the upload icon will pulse and you can reopen it here.</p>
          <button
            onClick={cancelProcess}
            disabled={busy}
            className="mt-2 px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-semibold disabled:opacity-50"
          >
            Cancel scan
          </button>
        </div>
      )}

      {/* ============ INVENTORY RAPID SCAN ============ */}
      {view === 'barcode' && !invReview && (
        <div className="flex-1 flex flex-col p-4 gap-3 overflow-y-auto">
          {/* Direction for new scans */}
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => setDirection('withdraw')}
              className={`flex-1 py-2 rounded-lg text-sm font-bold ${direction === 'withdraw' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
            >
              Take out
            </button>
            <button
              onClick={() => setDirection('deposit')}
              className={`flex-1 py-2 rounded-lg text-sm font-bold ${direction === 'deposit' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
            >
              Put back
            </button>
          </div>

          {/* Camera — tap to refocus */}
          <div className="relative w-full max-w-sm mx-auto" onClick={refocus}>
            <div id={INV_SCAN_REGION} className="w-full rounded-lg overflow-hidden bg-black" />
            {pulse && (
              <span
                key={pulse.id}
                className="absolute w-12 h-12 -ml-6 -mt-6 rounded-full border-2 border-white/80 animate-ping pointer-events-none"
                style={{ left: pulse.x, top: pulse.y }}
              />
            )}
            {toast && (
              <div className={`absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-bold text-white pointer-events-none ${toast.ok ? 'bg-emerald-600' : 'bg-amber-600'}`}>
                {toast.msg}
              </div>
            )}
          </div>
          <p className="text-zinc-500 text-xs text-center shrink-0">Keep scanning — tap the screen to refocus if blurry.</p>
          {error && <p className="text-red-400 text-xs text-center">{error}</p>}

          {/* Tally + actions */}
          <div className="mt-auto flex flex-col gap-2 shrink-0">
            <div className="text-center text-zinc-300 text-sm">
              <span className="font-bold text-white">{totalScanned}</span> scanned · {scanList.length} item{scanList.length === 1 ? '' : 's'}
            </div>
            <button
              onClick={() => setInvReview(true)}
              disabled={!scanList.length}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold disabled:opacity-50"
            >
              Review ({scanList.length})
            </button>
            <button
              onClick={() => { setError(null); setView('capture'); }}
              className="w-full py-2 text-zinc-400 text-sm font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ============ INVENTORY SCAN REVIEW ============ */}
      {view === 'barcode' && invReview && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {scanList.length === 0 && <p className="text-zinc-500 text-sm text-center mt-8">Nothing scanned.</p>}
            {scanList.map((r) => (
              <div key={r.key} className={`rounded-xl border p-3 ${r.known ? 'border-zinc-800 bg-zinc-900' : 'border-amber-500/40 bg-amber-500/[0.06]'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white font-semibold text-sm truncate">{r.name}</span>
                  <button onClick={() => removeLine(r.key)} className="text-zinc-500 hover:text-red-400 shrink-0"><X size={16} /></button>
                </div>
                {!r.known && (
                  <div className="mt-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wide text-amber-300 truncate">Not in inventory · {r.barcode}</span>
                      {addForm?.key !== r.key && (
                        <button
                          onClick={() => setAddForm({ key: r.key, name: '', quantity: 1, location: '', price: 0 })}
                          className="text-[11px] font-bold bg-amber-500/20 text-amber-200 px-2 py-1 rounded shrink-0"
                        >
                          Add new item
                        </button>
                      )}
                    </div>
                    {addForm?.key === r.key && (
                      <div className="mt-2 space-y-2">
                        <input
                          autoFocus
                          placeholder="Item name"
                          value={addForm.name}
                          onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                        />
                        <div className="flex gap-2">
                          <input type="number" placeholder="Qty" value={addForm.quantity} onChange={(e) => setAddForm({ ...addForm, quantity: e.target.value })} className="w-16 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm" />
                          <input placeholder="Location" value={addForm.location} onChange={(e) => setAddForm({ ...addForm, location: e.target.value })} className="flex-1 min-w-0 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm" />
                          <input type="number" placeholder="Price" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value })} className="w-20 bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white text-sm" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setAddForm(null)} className="flex-1 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-sm font-semibold">Cancel</button>
                          <button onClick={() => saveNewItem(r)} disabled={busy} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold disabled:opacity-50">Save item</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {r.known && (
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <button
                      onClick={() => toggleLineAction(r.key)}
                      className={`text-[11px] font-bold px-2 py-1 rounded ${r.action === 'withdraw' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}
                    >
                      {r.action === 'withdraw' ? 'Take out' : 'Put back'}
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setLineQty(r.key, r.quantity - 1)} className="w-8 h-8 rounded-lg bg-zinc-800 text-white font-bold">−</button>
                      <input
                        type="number"
                        value={r.quantity}
                        onChange={(e) => setLineQty(r.key, parseInt(e.target.value) || 0)}
                        className="w-14 text-center bg-gray-800 border border-gray-700 rounded-lg py-1.5 text-white"
                      />
                      <button onClick={() => setLineQty(r.key, r.quantity + 1)} className="w-8 h-8 rounded-lg bg-zinc-800 text-white font-bold">+</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {error && <p className="text-red-400 text-xs text-center">{error}</p>}
          </div>
          <div className="border-t border-zinc-800 p-3 flex gap-3 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
            <button
              onClick={() => setInvReview(false)}
              className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-xl font-bold flex items-center justify-center gap-2"
            >
              <Boxes size={17} /> Scan more
            </button>
            <button
              onClick={submitScan}
              disabled={busy || !scanList.some(r => r.known && r.quantity > 0)}
              className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
              Submit ({scanList.filter(r => r.known && r.quantity > 0).length})
            </button>
          </div>
        </>
      )}

      {/* ============ REVIEW ============ */}
      {view === 'review' && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            <p className="text-xs text-zinc-500 px-1">
              Check the receipts to act on, then Save them yourself or hand them to the PM.
            </p>
            {batchData.map((r) => {
              const checked = selected.includes(r.id);
              const detected = !!r.adminDetectedProject; // routed to a project (PO or return-original)
              return (
                <div
                  key={r.id}
                  className={`rounded-xl border p-3 transition-colors ${
                    checked ? 'border-indigo-500/50 bg-indigo-500/[0.06]' : 'border-zinc-800 bg-zinc-900'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSelect(r.id)}
                      className="mt-1 rounded border-gray-600 bg-gray-800 text-indigo-600 focus:ring-indigo-600 w-5 h-5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white font-semibold text-sm truncate">
                          {r.store || 'Unknown'}{r.isReturn && <span className="ml-1 text-orange-400 text-[10px] font-bold">RETURN</span>}
                        </span>
                        <span className="text-emerald-400 font-bold text-sm shrink-0">
                          ${Math.abs(Number(r.totalAmount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {r.date?.split('T')[0] || ''}{r.receiptNumber ? ` · #${r.receiptNumber}` : ''}
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {detected ? (
                          <>
                            <span className="text-[10px] font-bold bg-emerald-500/15 text-emerald-300 px-2 py-1 rounded">
                              → {r.adminDetectedTitle || `PO# ${r.adminDetectedPO || r.poNumber}`}{r.adminDetectedClient ? ` - ${r.adminDetectedClient}` : ''}
                            </span>
                            {/* PO was found → show it, locked (not editable). */}
                            <span className="text-[10px] font-bold text-zinc-300 bg-gray-800 border border-gray-700 px-2 py-1 rounded">
                              PO# {r.adminDetectedPO || r.poNumber}
                            </span>
                          </>
                        ) : (
                          <>
                            {r.manualPO && r.poNumber ? (
                              <span className="text-[10px] font-bold bg-indigo-500/15 text-indigo-300 px-2 py-1 rounded">
                                Manual → PO# {r.poNumber}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-300 px-2 py-1 rounded">
                                {r.reviewReason || 'NO PO Detected'}
                              </span>
                            )}
                            {/* PO not found → editable, numbers only. Blank/spaces = Unallocated. */}
                            <span className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1">
                              <span className="text-[10px] font-bold text-zinc-500">PO#</span>
                              <input
                                value={r.poNumber || ''}
                                onChange={(e) => updatePO(r.id, e.target.value.replace(/\D/g, ''))}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                placeholder="—"
                                className="w-16 bg-transparent text-xs text-white focus:outline-none"
                              />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {error && <p className="text-red-400 text-xs text-center">{error}</p>}

            {/* Cancel terminates this scan entirely (deletes the server batch). */}
            <button
              onClick={cancelProcess}
              disabled={busy}
              className="w-full py-2.5 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Trash2 size={15} /> Cancel scan &amp; discard
            </button>
          </div>

          {/* Aggregate total of everything scanned in this batch */}
          <div className="px-4 py-2.5 border-t border-zinc-800 flex items-center justify-between shrink-0">
            <span className="text-[11px] text-zinc-500 font-bold uppercase tracking-wide">
              {selectedCount} of {batchData.length} selected · total
            </span>
            <span className={`text-lg font-bold ${batchTotal < 0 ? 'text-amber-400' : 'text-emerald-300'}`}>
              {batchTotal < 0 ? '−' : ''}${Math.abs(batchTotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}{batchTotal < 0 ? ' credit' : ''}
            </span>
          </div>

          {/* Action bar */}
          <div className="border-t border-zinc-800 p-3 flex gap-3 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
            <button
              onClick={handleReroute}
              disabled={busy || !selectedCount}
              className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CornerUpRight size={17} /> Acknowledge Re-route
            </button>
            <button
              onClick={handleSave}
              disabled={busy || !selectedCount}
              className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
              Save ({selectedCount}) Expense
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
